import axios from 'axios';

const getAuthToken = (): string | null => {
  if (typeof document === 'undefined') return null;
  const cookies = document.cookie.split(';');
  for (const cookie of cookies) {
    const [name, value] = cookie.trim().split('=');
    if (name === 'session_token') {
      return value;
    }
  }
  return null;
};

const BFF_API_URL = import.meta.env.VITE_BFF_API_URL || 'http://localhost:8086';
const AUTH_APP_URL = import.meta.env.VITE_AUTH_APP_URL || 'http://localhost:3000';
const crmClient = axios.create({
  baseURL: BFF_API_URL,
  withCredentials: true,
  headers: { 'Content-Type': 'application/json' },
});

crmClient.interceptors.request.use(config => {
  const token = getAuthToken();
  if (token && config.headers) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

crmClient.interceptors.response.use(
  response => response,
  error => {
    if (error.response?.status === 401 && typeof window !== 'undefined') {
      window.location.href = AUTH_APP_URL;
    }
    const apiError = error.response?.data?.error;
    if (typeof apiError === 'string' && apiError.length > 0) {
      error.message = apiError;
    }
    return Promise.reject(error);
  },
);

export type LinkedEntityType =
  | 'event_booking'
  | 'group_event'
  | 'room_booking'
  | 'spa_appointment'
  | 'food_order';

export interface GuestInquiry {
  id: string;
  contact_name: string;
  organisation: string;
  email: string;
  phone: string;
  source: string;
  pipeline_stage: string;
  nature: string;
  liaison_id?: string;
  liaison_name: string;
  estimated_value?: number;
  linked_entity_type?: LinkedEntityType | string;
  linked_entity_id?: string;
  linked_reference?: string;
  booked_value?: number;
  linked_at?: string;
  notes: string;
  created_at: string;
  updated_at: string;
}

export interface SalesActivity {
  id: string;
  inquiry_id: string;
  inquiry_contact_name?: string;
  activity_type: string;
  description: string;
  performed_by: string;
  performed_at: string;
  created_at: string;
}

export interface CreateGuestInquiryInput {
  contact_name: string;
  organisation?: string;
  email?: string;
  phone?: string;
  source: string;
  pipeline_stage?: string;
  nature?: string;
  liaison_id?: string;
  liaison_name?: string;
  estimated_value?: number;
  notes?: string;
}

export interface UpdateGuestInquiryInput {
  contact_name?: string;
  organisation?: string;
  email?: string;
  phone?: string;
  source?: string;
  pipeline_stage?: string;
  nature?: string;
  liaison_id?: string;
  liaison_name?: string;
  estimated_value?: number;
  notes?: string;
}

export interface CreateSalesActivityInput {
  inquiry_id: string;
  activity_type: string;
  description: string;
  performed_by?: string;
}

export interface LinkInquiryInput {
  linked_entity_type: LinkedEntityType;
  linked_entity_id: string;
  linked_reference?: string;
  booked_value?: number;
  performed_by?: string;
  advance_to_won?: boolean;
}

export interface CrmListDateParams {
  date_from?: string;
  date_to?: string;
}

export const guestInquiryService = {
  list: (
    params?: {
      page?: number;
      page_size?: number;
      all?: boolean;
      stage?: string;
      source?: string;
      search?: string;
      linked?: 'linked' | 'unlinked';
    } & CrmListDateParams,
  ): Promise<{
    inquiries: GuestInquiry[];
    total: number;
    page?: number;
    page_size?: number;
    total_pages?: number;
  }> => crmClient.get('/api/v1/guest-inquiries', { params }).then(res => res.data),

  get: (id: string): Promise<GuestInquiry> =>
    crmClient.get(`/api/v1/guest-inquiries/${id}`).then(res => res.data),

  create: (data: CreateGuestInquiryInput): Promise<GuestInquiry> =>
    crmClient.post('/api/v1/guest-inquiries', data).then(res => res.data),

  update: (id: string, data: UpdateGuestInquiryInput): Promise<GuestInquiry> =>
    crmClient.put(`/api/v1/guest-inquiries/${id}`, data).then(res => res.data),

  delete: (id: string): Promise<{ message: string }> =>
    crmClient.delete(`/api/v1/guest-inquiries/${id}`).then(res => res.data),

  moveStage: (
    id: string,
    data: { pipeline_stage: string; performed_by?: string },
  ): Promise<GuestInquiry> =>
    crmClient.patch(`/api/v1/guest-inquiries/${id}/stage`, data).then(res => res.data),

  link: (id: string, data: LinkInquiryInput): Promise<GuestInquiry> =>
    crmClient.post(`/api/v1/guest-inquiries/${id}/link`, data).then(res => res.data),

  unlink: (id: string, data?: { performed_by?: string }): Promise<GuestInquiry> =>
    crmClient.delete(`/api/v1/guest-inquiries/${id}/link`, { data }).then(res => res.data),
};

export const salesActivityService = {
  list: (
    params?: {
      page?: number;
      page_size?: number;
      all?: boolean;
      inquiry_id?: string;
      type?: string;
    } & CrmListDateParams,
  ): Promise<{
    activities: SalesActivity[];
    total: number;
    page?: number;
    page_size?: number;
    total_pages?: number;
  }> => crmClient.get('/api/v1/sales-activities', { params }).then(res => res.data),

  create: (data: CreateSalesActivityInput): Promise<SalesActivity> =>
    crmClient.post('/api/v1/sales-activities', data).then(res => res.data),

  delete: (id: string): Promise<{ message: string }> =>
    crmClient.delete(`/api/v1/sales-activities/${id}`).then(res => res.data),
};

export type CommissionRunStatus = 'draft' | 'approved' | 'paid' | 'cancelled';

export interface LiaisonCommissionLine {
  id: string;
  payout_id: string;
  inquiry_id: string;
  guest_name: string;
  company: string;
  linked_reference: string;
  booked_value: number;
  linked_at?: string;
  commission_eligible: boolean;
  note: string;
}

export interface LiaisonCommissionPayout {
  id: string;
  run_id: string;
  liaison_id: string;
  liaison_name: string;
  liaison_email: string;
  eligible_revenue: number;
  commission_amount: number;
  linked_bookings: number;
  unattributed_wins: number;
  lines?: LiaisonCommissionLine[];
}

export interface LiaisonCommissionRun {
  id: string;
  period_label: string;
  date_from: string;
  date_to: string;
  commission_rate: number;
  status: CommissionRunStatus;
  total_eligible_revenue: number;
  total_commission: number;
  liaison_count: number;
  created_by: string;
  approved_by: string;
  approved_at?: string;
  paid_by: string;
  paid_at?: string;
  notes: string;
  created_at: string;
  updated_at: string;
  payouts?: LiaisonCommissionPayout[];
}

export type LiaisonCampaignStatus =
  | 'planning'
  | 'active'
  | 'closed'
  | 'approved'
  | 'paid'
  | 'cancelled';

export interface LiaisonCampaignMember {
  id: string;
  campaign_id: string;
  liaison_id: string;
  liaison_name: string;
  liaison_email: string;
}

export interface LiaisonCampaignLine {
  id: string;
  payout_id: string;
  inquiry_id: string;
  guest_name: string;
  company: string;
  linked_reference: string;
  booked_value: number;
  linked_at?: string;
  commission_eligible: boolean;
  note: string;
}

export interface LiaisonCampaignPayout {
  id: string;
  campaign_id: string;
  liaison_id: string;
  liaison_name: string;
  liaison_email: string;
  eligible_revenue: number;
  commission_amount: number;
  linked_bookings: number;
  unattributed_wins: number;
  lines?: LiaisonCampaignLine[];
}

export interface LiaisonCampaign {
  id: string;
  name: string;
  agenda: string;
  goal_description: string;
  goal_revenue?: number;
  date_from: string;
  date_to: string;
  commission_rate: number;
  status: LiaisonCampaignStatus;
  total_eligible_revenue: number;
  total_commission: number;
  member_count: number;
  created_by: string;
  approved_by: string;
  approved_at?: string;
  paid_by: string;
  paid_at?: string;
  closed_by: string;
  closed_at?: string;
  notes: string;
  created_at: string;
  updated_at: string;
  members?: LiaisonCampaignMember[];
  payouts?: LiaisonCampaignPayout[];
}

export const liaisonCampaignService = {
  list: (): Promise<{ campaigns: LiaisonCampaign[]; total: number }> =>
    crmClient.get('/api/v1/liaison-campaigns').then(res => res.data),

  get: (id: string): Promise<LiaisonCampaign> =>
    crmClient.get(`/api/v1/liaison-campaigns/${id}`).then(res => res.data),

  create: (data: {
    name: string;
    agenda?: string;
    goal_description?: string;
    goal_revenue?: number;
    date_from: string;
    date_to: string;
    commission_rate?: number;
    notes?: string;
    created_by?: string;
  }): Promise<LiaisonCampaign> =>
    crmClient.post('/api/v1/liaison-campaigns', data).then(res => res.data),

  update: (
    id: string,
    data: Partial<{
      name: string;
      agenda: string;
      goal_description: string;
      goal_revenue: number;
      date_from: string;
      date_to: string;
      commission_rate: number;
      notes: string;
    }>,
  ): Promise<LiaisonCampaign> =>
    crmClient.put(`/api/v1/liaison-campaigns/${id}`, data).then(res => res.data),

  activate: (id: string): Promise<LiaisonCampaign> =>
    crmClient.post(`/api/v1/liaison-campaigns/${id}/activate`).then(res => res.data),

  addMember: (
    id: string,
    data: { liaison_id: string; liaison_name: string; liaison_email?: string },
  ): Promise<LiaisonCampaign> =>
    crmClient.post(`/api/v1/liaison-campaigns/${id}/members`, data).then(res => res.data),

  removeMember: (id: string, liaisonId: string): Promise<LiaisonCampaign> =>
    crmClient.delete(`/api/v1/liaison-campaigns/${id}/members/${liaisonId}`).then(res => res.data),

  preview: (id: string): Promise<LiaisonCampaign> =>
    crmClient.get(`/api/v1/liaison-campaigns/${id}/preview`).then(res => res.data),

  close: (id: string, actor?: string): Promise<LiaisonCampaign> =>
    crmClient.post(`/api/v1/liaison-campaigns/${id}/close`, { actor }).then(res => res.data),

  approve: (id: string, actor?: string): Promise<LiaisonCampaign> =>
    crmClient.post(`/api/v1/liaison-campaigns/${id}/approve`, { actor }).then(res => res.data),

  markPaid: (id: string, actor?: string): Promise<LiaisonCampaign> =>
    crmClient.post(`/api/v1/liaison-campaigns/${id}/pay`, { actor }).then(res => res.data),

  cancel: (id: string): Promise<LiaisonCampaign> =>
    crmClient.delete(`/api/v1/liaison-campaigns/${id}`).then(res => res.data),
};

export const liaisonCommissionService = {
  list: (): Promise<{ runs: LiaisonCommissionRun[]; total: number }> =>
    crmClient.get('/api/v1/liaison-commissions').then(res => res.data),

  preview: (params: {
    date_from: string;
    date_to: string;
    commission_rate: number;
  }): Promise<LiaisonCommissionRun> =>
    crmClient.get('/api/v1/liaison-commissions/preview', { params }).then(res => res.data),

  get: (id: string): Promise<LiaisonCommissionRun> =>
    crmClient.get(`/api/v1/liaison-commissions/${id}`).then(res => res.data),

  create: (data: {
    date_from: string;
    date_to: string;
    commission_rate: number;
    period_label?: string;
    created_by?: string;
    notes?: string;
  }): Promise<LiaisonCommissionRun> =>
    crmClient.post('/api/v1/liaison-commissions', data).then(res => res.data),

  approve: (id: string, actor?: string): Promise<LiaisonCommissionRun> =>
    crmClient.post(`/api/v1/liaison-commissions/${id}/approve`, { actor }).then(res => res.data),

  markPaid: (id: string, actor?: string): Promise<LiaisonCommissionRun> =>
    crmClient.post(`/api/v1/liaison-commissions/${id}/pay`, { actor }).then(res => res.data),

  cancel: (id: string): Promise<LiaisonCommissionRun> =>
    crmClient.delete(`/api/v1/liaison-commissions/${id}`).then(res => res.data),
};
